import { useTranslation } from "react-i18next"
import { Warning } from "@phosphor-icons/react"
import { Button } from "@/components/ui/button"
import {
  PREVIEW_ERROR_KEY,
  PREVIEW_STAGE,
  PREVIEW_STATUS,
  PreviewErrorKey,
  PreviewStage,
  PreviewStatus,
} from "@/modules/patients/detail/record-extract/types/record-extract-types"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { SofiaEnvironment } from "@/sofia-environment"

interface ExtractPreviewPaneProps {
  status: PreviewStatus
  stage: PreviewStage
  summaryFileId: string | null
  errorMessageKey: PreviewErrorKey
  onRetry: VoidFunction
}

const CenteredState = ({ children }: { children: React.ReactNode }) => (
  <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 py-12 text-center">
    <div className="flex w-full max-w-100 flex-col items-center gap-4">{children}</div>
  </div>
)

const StateText = ({ title, description }: { title: string; description: string }) => (
  <div className="flex flex-col gap-1 text-center">
    <p className="text-on-surface text-xl font-bold" data-testid="record-extract-preview-title">
      {title}
    </p>
    <p className="text-on-surface-subdued text-base" data-testid="record-extract-preview-description">
      {description}
    </p>
  </div>
)

export const ExtractPreviewPane = ({
  status,
  stage,
  summaryFileId,
  errorMessageKey,
  onRetry,
}: ExtractPreviewPaneProps) => {
  const { t } = useTranslation()
  const countryCode = useCountryParam()

  const renderContent = () => {
    switch (status) {
      case PREVIEW_STATUS.generating: {
        const stageMessageKey =
          stage === PREVIEW_STAGE.fetching
            ? "patient.recordExtract.preview.fetching"
            : "patient.recordExtract.preview.generating"
        return (
          <CenteredState>
            <Button variant="tertiary" isLoading className="pointer-events-none h-12 w-12 p-0" />
            <StateText title={t(`${stageMessageKey}.title`)} description={t(`${stageMessageKey}.description`)} />
          </CenteredState>
        )
      }
      case PREVIEW_STATUS.loaded:
        if (!summaryFileId) return null
        return (
          <embed
            className="h-full w-full"
            type="application/pdf"
            src={`${SofiaEnvironment.Bff.Url}/api/${countryCode.toLowerCase()}/files/${summaryFileId}?embed=true#view=fitH`}
            data-testid="record-extract-preview-pdf"
          />
        )
      case PREVIEW_STATUS.error:
        return (
          <CenteredState>
            <Warning className="text-critical h-12 w-12" weight="fill" />
            <StateText title={t(`${errorMessageKey}.title`)} description={t(`${errorMessageKey}.description`)} />
            <Button
              variant="secondary"
              onClick={onRetry}
              disabled={errorMessageKey === PREVIEW_ERROR_KEY.noContact}
              data-testid="record-extract-retry">
              {t("common.button.retry")}
            </Button>
          </CenteredState>
        )
      case PREVIEW_STATUS.stale:
      case PREVIEW_STATUS.idle:
      default:
        return (
          <CenteredState>
            <StateText
              title={t("patient.recordExtract.preview.empty.title")}
              description={t("patient.recordExtract.preview.empty.description")}
            />
          </CenteredState>
        )
    }
  }

  return (
    <div
      className="border-border flex min-h-0 flex-1 flex-col overflow-clip rounded-xl border"
      data-testid="record-extract-preview-pane"
      data-status={status}>
      {renderContent()}
    </div>
  )
}
